import type { VatCategory } from "@/api/types";
import {
  ZERO,
  add,
  compare,
  decimal,
  divide,
  multiply,
  round,
  subtract,
  toFixed,
  type Decimal,
} from "./decimal";

/**
 * The EN 16931 / BIS 3.0 calculation chain, in the browser.
 *
 * This is the second implementation — `Rigo.Accounting.Sales.Calculation.InvoiceCalculator` is the
 * first. Both are tested against `conformance/invoice-totals.json`, because two implementations of
 * a specified chain drift, and byte-comparing them would prove nothing: they are not meant to be
 * identical, they are meant to behave identically. The Bikram Sambat calendar is why that
 * distinction is written down — the legacy system had three conversion tables and they disagreed.
 *
 * Why it exists at all: the line editor shows a running total while somebody types. Computing that
 * on the server per keystroke is a round trip per character; computing it differently from the
 * server means the total changes when they press Save.
 */
export interface LineInput {
  category: VatCategory;
  /** Null only for category `O`, which carries no rate at all. */
  rate: string | null;
  /** BT-148. Always positive — a credit expresses its sign through quantity. */
  grossPrice: string;
  priceDiscount?: string;
  /** BT-149. One unless the price is quoted per some other number of units. */
  priceBaseQuantity?: string;
  quantity: string;
  charges?: string;
  allowances?: string;
}

export interface AdjustmentInput {
  isCharge: boolean;
  amount: string;
  category: VatCategory;
  rate: string | null;
}

export interface BreakdownRow {
  category: VatCategory;
  rate: string | null;
  taxableAmount: string;
  taxAmount: string;
}

export interface Totals {
  sumOfLineNetAmounts: string;
  sumOfAllowances: string;
  sumOfCharges: string;
  totalWithoutVat: string;
  totalVatAmount: string;
  totalWithVat: string;
  paidAmount: string;
  roundingAmount: string;
  amountDueForPayment: string;
  breakdown: BreakdownRow[];
}

/** Category `O` carries no rate; every other category does. */
function carriesRate(category: VatCategory): boolean {
  return category !== "O";
}

/**
 * BT-131. The `price ÷ base quantity × quantity` product rounds **first**; line charges and
 * allowances, themselves already rounded, are then applied to the rounded figure.
 *
 * Rounding the whole expression once instead gives a different answer on ordinary invoices, and the
 * validator on the receiving network computes it this way.
 *
 * @returns the line net amount, or null when the price base quantity is zero — which the legacy
 * system divided by without checking (`05-nepal-compliance.md` RULE 3.4).
 */
export function lineNetAmount(line: LineInput): string | null {
  const base = decimal(line.priceBaseQuantity ?? "1");
  const netPrice = subtract(decimal(line.grossPrice), decimal(line.priceDiscount ?? "0"));

  const perUnit = divide(netPrice, base);
  if (perUnit === null) return null;

  const product = round(multiply(perUnit, decimal(line.quantity)), 2);
  const withCharges = add(product, round(decimal(line.charges ?? "0"), 2));

  return toFixed(subtract(withCharges, round(decimal(line.allowances ?? "0"), 2)), 2);
}

/**
 * The whole totals block (BG-22).
 *
 * Figures derived from already-rounded inputs are **not** rounded again — total with VAT and amount
 * due inherit their precision. Rounding them a second time is how a total drifts a paisa away from
 * the sum of its own parts, which is the one thing a person reading an invoice will always notice.
 */
export function calculateTotals(
  lines: LineInput[],
  adjustments: AdjustmentInput[] = [],
  options: { paid?: string; roundingAmount?: string } = {},
): Totals {
  const netAmounts = lines.map((line) => decimal(lineNetAmount(line) ?? "0"));

  const sumOfLines = netAmounts.reduce(add, ZERO);
  const sumOfAllowances = adjustments
    .filter((a) => !a.isCharge)
    .reduce((total, a) => add(total, round(decimal(a.amount), 2)), ZERO);
  const sumOfCharges = adjustments
    .filter((a) => a.isCharge)
    .reduce((total, a) => add(total, round(decimal(a.amount), 2)), ZERO);

  const totalWithoutVat = add(subtract(sumOfLines, sumOfAllowances), sumOfCharges);
  const breakdown = buildBreakdown(lines, netAmounts, adjustments);
  const totalVat = breakdown.reduce((total, row) => add(total, decimal(row.taxAmount)), ZERO);

  const totalWithVat = add(totalWithoutVat, totalVat);
  const paid = decimal(options.paid ?? "0");
  const roundingAmount = decimal(options.roundingAmount ?? "0");
  const amountDue = add(subtract(totalWithVat, paid), roundingAmount);

  return {
    sumOfLineNetAmounts: toFixed(sumOfLines),
    sumOfAllowances: toFixed(sumOfAllowances),
    sumOfCharges: toFixed(sumOfCharges),
    totalWithoutVat: toFixed(totalWithoutVat),
    totalVatAmount: toFixed(totalVat),
    totalWithVat: toFixed(totalWithVat),
    paidAmount: toFixed(paid),
    roundingAmount: toFixed(roundingAmount),
    amountDueForPayment: toFixed(amountDue),
    breakdown,
  };
}

/**
 * BG-23. One row per distinct **category and rate** pair, across lines and document-level
 * allowances and charges alike.
 *
 * The grouping key is the rate's numeric value, not its text. The brief warns that a line at `25`
 * and a line at `25.00` belong to the same row and that "if your grouping key is a string, this
 * will bite" — so `"25"` and `"25.00"` compare equal here, and a `Map` keyed on the string would
 * have produced two rows and two tax amounts.
 */
function buildBreakdown(
  lines: LineInput[],
  netAmounts: Decimal[],
  adjustments: AdjustmentInput[],
): BreakdownRow[] {
  const groups: Array<{
    category: VatCategory;
    rate: Decimal;
    rateText: string | null;
    taxable: Decimal;
  }> = [];

  const find = (category: VatCategory, rate: string | null) => {
    const value = carriesRate(category) ? decimal(rate ?? "0") : ZERO;
    let group = groups.find((g) => g.category === category && compare(g.rate, value) === 0);
    if (!group) {
      group = {
        category,
        rate: value,
        rateText: carriesRate(category) ? (rate ?? "0") : null,
        taxable: ZERO,
      };
      groups.push(group);
    }
    return group;
  };

  lines.forEach((line, index) => {
    const group = find(line.category, line.rate);
    group.taxable = add(group.taxable, netAmounts[index] ?? ZERO);
  });

  for (const adjustment of adjustments) {
    const group = find(adjustment.category, adjustment.rate);
    const amount = round(decimal(adjustment.amount), 2);
    group.taxable = adjustment.isCharge
      ? add(group.taxable, amount)
      : subtract(group.taxable, amount);
  }

  const hundred = decimal("100");

  return groups.map((group) => ({
    category: group.category,
    rate: group.rateText,
    taxableAmount: toFixed(group.taxable),
    // BT-117 = BT-116 × rate ÷ 100, rounded once. Out of scope is always zero tax.
    taxAmount: carriesRate(group.category)
      ? toFixed(round(divide(multiply(group.taxable, group.rate), hundred) ?? ZERO, 2))
      : "0.00",
  }));
}
